import { useEffect, useRef } from "react";

const DEFAULT_READ_THRESHOLD = 0.8;
const DEFAULT_MIN_TIME_MS = 15000;

/**
 * Track scroll/read progress for long-form content.
 * Calls `onComplete` once when the user scrolls past the threshold
 * and has spent at least `minTimeMs` on the element.
 *
 * @param {object} options
 * @param {React.RefObject<HTMLElement>} options.ref - element to observe
 * @param {() => void} options.onComplete - callback when read threshold is crossed
 * @param {number} options.threshold - scroll depth fraction (0-1), default 0.8
 * @param {number} options.minTimeMs - minimum dwell time in ms, default 15000
 */
export const useScrollProgress = ({
  ref,
  onComplete,
  threshold = DEFAULT_READ_THRESHOLD,
  minTimeMs = DEFAULT_MIN_TIME_MS,
}) => {
  const completedRef = useRef(false);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    const element = ref?.current;
    if (!element) return;

    startTimeRef.current = Date.now();
    completedRef.current = false;

    const handleScroll = () => {
      if (completedRef.current) return;

      const scrollTop = element.scrollTop;
      const scrollHeight = element.scrollHeight - element.clientHeight;
      if (scrollHeight <= 0) return;

      const scrolled = scrollTop / scrollHeight;
      const elapsed = Date.now() - startTimeRef.current;

      if (scrolled >= threshold && elapsed >= minTimeMs) {
        completedRef.current = true;
        onComplete?.();
      }
    };

    element.addEventListener("scroll", handleScroll, { passive: true });
    return () => element.removeEventListener("scroll", handleScroll);
  }, [ref, onComplete, threshold, minTimeMs]);
};

export default useScrollProgress;
